import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { BarChart } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { getIndustryI18nKey } from '../utils/industryMapping'
import { dataService } from '../services/dataService'
import { PenetrationResult } from '../services/penetrationCalculator'
import CircularProgress from '../components/CircularProgress'
import TrendChart from '../components/TrendChart'
import AITimeline from '../components/AITimeline'
import AICapabilitiesRadar from '../components/AICapabilitiesRadar'
import AIScenarios from '../components/AIScenarios'
import AIToolsNavigation from '../components/AIToolsNavigation'
import AIFutureInteraction from '../components/AIFutureInteraction'
import DailyChangeWidget from '../components/DailyChangeWidget'
import DashboardNewsWidget from '../components/DashboardNewsWidget'
import IndustryBreakdownChart from '../components/IndustryBreakdownChart'

interface EnhancedPenetrationResult extends PenetrationResult {
  dailyChange: number;
}

const Home = () => {
  const [penetrationResult, setPenetrationResult] = useState<EnhancedPenetrationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await dataService.getPenetrationData();
        const simulatedDailyChange = (Math.random() * 0.2 - 0.05) / 100;
        const enhancedResult: EnhancedPenetrationResult = {
          ...result,
          dailyChange: simulatedDailyChange
        };
        setPenetrationResult(enhancedResult);
      } catch (error) {
        console.error("❌ 在Home组件中加载数据失败:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const industryPenetrationData = penetrationResult?.industryBreakdown || [];

  return (
    <div className="min-h-screen bg-vintage-paper text-vintage-ink">
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              {t('home.mainTitle')}
            </h1>
            <p className="font-serif text-lg sm:text-xl text-vintage-muted mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
              {t('home.mainSubtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              <span className="px-3 py-1 bg-vintage-paper border border-vintage-accent text-vintage-accent rounded-sm text-sm font-serif">
                {t('home.dataMonitoring')}
              </span>
              <span className="px-3 py-1 bg-vintage-paper border border-vintage-accent text-vintage-accent rounded-sm text-sm font-serif">
                {t('home.toolNavigation')}
              </span>
              <span className="px-3 py-1 bg-vintage-paper border border-vintage-accent text-vintage-accent rounded-sm text-sm font-serif">
                {t('home.communityDiscussion')}
              </span>
              <span className="px-3 py-1 bg-vintage-paper border border-vintage-accent text-vintage-accent rounded-sm text-sm font-serif">
                {t('home.futurePrediction')}
              </span>
            </div>
          </motion.div>

          {/* 主要数据展示区域 - 响应式三栏布局 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8 sm:mb-12 items-stretch">
            {/* 左侧行业数据 */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="space-y-3 lg:order-1 order-2 h-full"
            >
              <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-4 shadow-md h-full flex flex-col">
                <h3 className="font-display text-sm font-semibold mb-3 flex items-center">
                  <BarChart className="w-4 h-4 mr-2 text-vintage-accent" />
                  {t('home.industryRankingTitle')}
                </h3>
                <div className="flex-1 overflow-y-auto pr-2">
                  {loading ? (
                    <div className="space-y-2">
                      {[...Array(10)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-3 bg-gray-200 rounded mb-1 w-1/2"></div>
                          <div className="h-2 bg-gray-100 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {industryPenetrationData.map((industry) => {
                        return (
                          <div key={industry.name} className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-gray-600 mb-1">{t(getIndustryI18nKey(industry.name))}</p>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="h-2 bg-gradient-to-r from-ai-500 to-primary-500 rounded-full transition-all duration-1000"
                                  style={{ width: `${(industry.penetration * 100)}%` }}
                                ></div>
                              </div>
                            </div>
                            <span className="text-sm font-bold text-ai-600 ml-3">
                              {Math.round(industry.penetration * 100)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* 中间渗透率仪表盘 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="flex justify-center lg:order-2 order-1"
            >
              <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-4 sm:p-6 lg:p-8 w-full">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="font-display text-xl sm:text-2xl font-bold mb-2">{t('home.globalPenetrationTitle')}</h2>
                  <p className="font-serif text-sm sm:text-base text-vintage-muted px-2">{t('home.comprehensiveCalculation')}</p>
                </div>
                <div className="flex justify-center items-center min-h-[220px] sm:min-h-[280px] py-4">
                  <CircularProgress value={penetrationResult?.overallPenetration || 0} loading={loading} />
                </div>
                <div className="mt-4 sm:mt-6 text-center space-y-3">
                  <DailyChangeWidget change={penetrationResult?.dailyChange || 0} loading={loading} />
                  <p className="text-xs sm:text-sm text-gray-500">
                    {t('home.lastUpdated')}: {penetrationResult ? new Date(penetrationResult.lastUpdated).toLocaleString() : t('home.loading')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* 右侧新闻数据 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="lg:order-3 order-3 h-full"
            >
              <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-4 shadow-md h-full">
                <DashboardNewsWidget />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* AI工具导航区域 */}
      <div className="bg-vintage-paper border-y border-vintage-muted py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">{t('home.toolNavigation')}</h2>
            <p className="font-serif text-vintage-muted">{t('home.toolNavigationSubtitle')}</p>
          </div>
          <AIToolsNavigation />
        </div>
      </div>

      <div className="bg-vintage-paper py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-4">{t('home.trends')}</h2>
            <p className="font-serif text-vintage-muted">{t('trend.subtitle')}</p>
          </motion.div>

          <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-6 shadow-md">
            <TrendChart />
          </div>
        </div>
      </div>

      <div className="bg-vintage-paper border-t border-vintage-muted py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-6 shadow-md">
            <AITimeline />
          </div>
        </div>
      </div>

      <div className="bg-vintage-paper py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-6 shadow-md">
            <AICapabilitiesRadar />
          </div>
        </div>
      </div>

      <div className="bg-vintage-paper border-t border-vintage-muted py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-sm border border-vintage-muted p-6 shadow-md">
            <AIScenarios />
          </div>
        </div>
      </div>

      <div className="bg-vintage-accent py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-vintage-paper mb-4">{t('common.exploreGlobalImpact')}</h2>
            <p className="font-serif text-vintage-paper/90 mb-6 sm:mb-8 px-4">
              {t('common.discoverDetailed')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
              <motion.a
                href="/industries"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-serif rounded-sm text-vintage-accent bg-vintage-paper hover:bg-white transition-colors"
              >
                {t('nav.industries')}
              </motion.a>
              <motion.a
                href="/countries"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-vintage-paper text-base font-serif rounded-sm text-vintage-paper hover:bg-vintage-paper hover:text-vintage-accent transition-colors"
              >
                {t('nav.countries')}
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Home; 